import { Button } from "@/components/ui/button";
import { useDownloads, type Download } from "@/contexts/DownloadsContext";
import { trunkReportService } from "@/services/api/trunkReport";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  CheckCircle,
  Clock,
  DownloadIcon,
  ExternalLink,
  FileText,
  Trash2,
  XCircle,
} from "lucide-react";
import type React from "react";
import { useEffect, useRef } from "react";

interface DownloadsDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  onViewAll?: () => void;
}

const DownloadsDropdown: React.FC<DownloadsDropdownProps> = ({
  isOpen,
  onClose,
}) => {
  const { getRecentDownloads, removeDownload, deleteBackendFile } =
    useDownloads();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const recentDownloads = getRecentDownloads(10);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  const getStatusIcon = (status: Download["status"]) => {
    switch (status) {
      case "pending":
      case "processing":
        return <Clock size={16} className="text-yellow-500 animate-pulse" />;
      case "completed":
        return <CheckCircle size={16} className="text-green-500" />;
      case "failed":
        return <XCircle size={16} className="text-red-500" />;
      default:
        return <FileText size={16} className="text-gray-500" />;
    }
  };

  const getStatusText = (status: Download["status"]): string => {
    switch (status) {
      case "pending":
        return "Aguardando...";
      case "processing":
        return "Processando...";
      case "completed":
        return "Concluído";
      case "failed":
        return "Falhou";
      default:
        return status;
    }
  };

  const formatFileSize = (bytes: number | undefined): string => {
    if (!bytes) return "";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (
      Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
    );
  };

  const handleDownload = async (download: Download): Promise<void> => {
    if (download.status === "completed" && download.backendFileId) {
      try {
        const blob = await trunkReportService.downloadFile(
          download.backendFileId,
        );
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = download.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Erro ao baixar o arquivo:", error);
      }
    }
  };

  const handleRemove = async (download: Download): Promise<void> => {
    if (download.backendFileId) {
      await deleteBackendFile(download.backendFileId);
    } else {
      removeDownload(download.id);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={dropdownRef}
      className="absolute left-4 bottom-8 mb-2 w-96 bg-white dark:bg-gray-800 rounded-lg shadow-lg z-90 border border-gray-200 dark:border-gray-700"
    >
      <div className="p-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
            <DownloadIcon size={16} /> Downloads Recentes
          </h3>
          <Button
            variant="link"
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 h-auto p-0"
            asChild
          >
            <a
              href="/downloads"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1"
            >
              Ver todos
              <ExternalLink size={12} />
            </a>
          </Button>
        </div>
      </div>
      <div className="max-h-80 overflow-y-auto">
        {recentDownloads.length > 0 ? (
          recentDownloads.map((download) => (
            <div
              key={download.id}
              className="p-3 border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {getStatusIcon(download.status)}
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {getStatusText(download.status)}
                    </span>
                  </div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {download.filename}
                  </div>
                  {download.size && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {formatFileSize(download.size)}
                    </div>
                  )}
                  {download.metadata?.vehicleInfo && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {download.metadata.vehicleInfo}
                    </div>
                  )}
                  {download.metadata?.period && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {download.metadata.period}
                    </div>
                  )}
                  <div className="text-xs text-gray-400 mt-1">
                    {formatDistanceToNow(download.createdAt, {
                      addSuffix: true,
                      locale: ptBR,
                    })}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  {download.status === "completed" && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownload(download)}
                      className="text-xs h-7 dark:bg-gray-800 dark:text-white hover:bg-gray-100"
                    >
                      Baixar
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRemove(download)}
                    className="text-xs h-7 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <Trash2 size={12} className="mr-1" />
                    Remover
                  </Button>
                </div>
              </div>
              {download.status === "processing" && download.progress && (
                <div className="mt-2">
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                    <div
                      className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                      style={{ width: `${download.progress}%` }}
                    />
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {download.progress}% concluído
                  </div>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="p-4 text-center">
            <DownloadIcon size={32} className="mx-auto text-gray-400 mb-2" />
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Nenhum download recente
            </div>
          </div>
        )}
      </div>
      {recentDownloads.length > 0 && (
        <div className="p-2 border-t border-gray-200 dark:border-gray-700 text-center">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Mostrando {Math.min(recentDownloads.length, 10)} de{" "}
            {recentDownloads.length} downloads
          </div>
        </div>
      )}
    </div>
  );
};

export default DownloadsDropdown;
